/**
 * SettingsCard / SettingsRow — the one pattern for a settings section on both
 * web portals (design approved 26 Sep 2026): a card with a 16 px semibold
 * title and a 14 px line under it, then rows — an icon tile, a 14 px medium
 * label (with an optional badge), a 14 px muted detail, and the row's action
 * on the right. Actions are small outline (or quiet text) buttons; a change
 * that needs a form opens a dialog rather than growing the row.
 *
 * Texts are passed in translated, like everything this package renders.
 */

import type { ReactNode } from 'react';

import { cn } from '../lib/utils';

export function SettingsCard({
  title,
  description,
  action,
  children,
  className,
  ...rest
}: {
  title: string;
  description?: string;
  /** A card-wide action beside the title ("Sign out of other devices"). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <section
      className={cn('overflow-hidden rounded-xl border border-border bg-card', className)}
      data-testid={rest['data-testid']}
    >
      <header className="flex flex-wrap items-start justify-between gap-4 px-6 pb-4 pt-5">
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-snug text-foreground">{title}</h2>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {action}
      </header>
      <ul className="divide-y divide-border border-t border-border">{children}</ul>
    </section>
  );
}

export function SettingsRow({
  icon,
  iconTone = 'brand',
  title,
  badge,
  detail,
  action,
  className,
  ...rest
}: {
  icon: ReactNode;
  /** `brand`: Purple 02 tile (Tonnie's own); `neutral`: an outlined tile (a third party's mark). */
  iconTone?: 'brand' | 'neutral';
  title: string;
  badge?: ReactNode;
  detail?: ReactNode;
  action?: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    <li
      className={cn('flex items-center gap-4 px-6 py-4', className)}
      data-testid={rest['data-testid']}
    >
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]',
          iconTone === 'brand'
            ? 'bg-(--tonnie-purple-02) text-primary-accessible'
            : 'border border-border'
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">{title}</span>
          {badge}
        </div>
        {detail && <div className="mt-0.5 truncate text-sm text-muted-foreground">{detail}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </li>
  );
}

/** A small label inside a card's list ("Connected accounts"). */
export function SettingsSubheading({ children }: { children: ReactNode }) {
  return <li className="px-6 pb-2 pt-4 text-sm font-semibold text-muted-foreground">{children}</li>;
}

/** The pill beside a row's title: brand ("Sign-in & messages") or success ("Connected"). */
export function SettingsBadge({
  tone = 'brand',
  children,
}: {
  tone?: 'brand' | 'success';
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium',
        tone === 'brand'
          ? 'bg-(--tonnie-purple-02) text-primary-accessible'
          : 'bg-success/10 text-success-accessible'
      )}
    >
      {children}
    </span>
  );
}
